import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import {
  TelephoneInboundFill,
  TelephoneXFill,
  TelephoneFill,
  MicFill,
  MicMuteFill,
  JournalText,
  SendFill,
  PersonFill,
} from 'react-bootstrap-icons';
import { getGlobalVoiceToken, voiceAgentHeartbeat, voiceAgentLeave, callerLookup, getContactNotes, createContactNote } from '../../api/communicationApi';
import { useAuth } from '../userManagement/AuthContext';
import { hasProtectedContacts, maskedPhone } from '../../utils/customerPrivacy';
import { useNavigate } from 'react-router-dom';
import ConversationView from './ConversationView';
import LiveCallCoach from './LiveCallCoach';
import CallDeviceSettings from './CallDeviceSettings';
import CallAudioOutput from './CallAudioOutput';
import useCallDevice from './useCallDevice';
import { buildVoiceIdentity, createVoiceReceiver } from '../../utils/voiceReception';
import { closeCallNotifications, showCallNotification } from '../../utils/voiceDevice';
import { canReceiveCalls } from '../../utils/customerPrivacy';
import T, { useAutoTranslate } from '../common/T';



/**
 * Build a Twilio client identity that is UNIQUE per browser session, so two
 * people signed into the same account (or the same user in two tabs) each ring.
 * The suffix is stored in sessionStorage → stable across refreshes within a tab,
 * distinct between tabs/browsers.
 */


/**
 * Global incoming call banner — mounted in Layout.js so it works on every page.
 * Registers a Twilio Device with the user's identity and listens for incoming calls.
 * Shows a floating panel in the top-right corner when a call comes in.
 * Includes customer context: repair process IDs, recent messages, and note-taking.
 */
let sharedRingtoneAudioCtx = null;
let ringtoneAudioUnlocked = false;

const getRingtoneAudioContext = () => {
  if (typeof window === 'undefined') return null;
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return null;
  if (!sharedRingtoneAudioCtx || sharedRingtoneAudioCtx.state === 'closed') {
    sharedRingtoneAudioCtx = new AudioCtx();
    const context = sharedRingtoneAudioCtx;
    context.addEventListener?.('statechange', () => {
      if (context !== sharedRingtoneAudioCtx) return;
      ringtoneAudioUnlocked = context.state === 'running';
      window.dispatchEvent(new Event('voice-audio-state'));
    });
  }
  return sharedRingtoneAudioCtx;
};

const unlockRingtoneAudio = async () => {
  const audioCtx = getRingtoneAudioContext();
  if (!audioCtx) return false;

  try {
    if (audioCtx.state !== 'running') {
      await audioCtx.resume();
    }

    // Mobile browsers only unlock audio after a real user gesture.
    const gain = audioCtx.createGain();
    const oscillator = audioCtx.createOscillator();
    gain.gain.value = 0.0001;
    oscillator.frequency.value = 440;
    oscillator.connect(gain);
    gain.connect(audioCtx.destination);
    oscillator.start();
    oscillator.stop(audioCtx.currentTime + 0.03);
    oscillator.onended = () => { oscillator.disconnect(); gain.disconnect(); };
    ringtoneAudioUnlocked = audioCtx.state === 'running';
    return ringtoneAudioUnlocked;
  } catch (e) {
    console.warn('[Ringtone] Could not unlock mobile audio:', e);
    return false;
  }
};

const notifyCommunicationRefresh = () => {
  try {
    window.dispatchEvent(new CustomEvent('communication-tunnel-refresh'));
  } catch (_) {
    // Refresh notifications are best-effort in older browser contexts.
  }
};

// Ringtone generator using an audio context that is unlocked on first user touch/click.
const createRingtone = () => {
  let audioCtx = null;
  let intervalId = null;
  let vibrationId = null;
  let activeOscillators = [];
  let activeGains = [];

  const stopTone = () => {
    activeOscillators.forEach((osc) => {
      try { osc.stop(); } catch (_) {
        // Oscillator may already be stopped.
      }
      try { osc.disconnect(); } catch (_) {
        // Oscillator may already be disconnected.
      }
    });
    activeOscillators = [];
    activeGains.forEach((gain) => { try { gain.disconnect(); } catch (_) { /* already closed */ } });
    activeGains = [];
  };

  const ringBurst = () => {
    if (!audioCtx || audioCtx.state !== 'running') return;
    stopTone();

    const gainNode = audioCtx.createGain();
    const osc1 = audioCtx.createOscillator();
    const osc2 = audioCtx.createOscillator();
    const now = audioCtx.currentTime;

    osc1.type = 'sine';
    osc2.type = 'sine';
    osc1.frequency.value = 440;
    osc2.frequency.value = 480;
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.16, now + 0.05);
    gainNode.gain.setValueAtTime(0.16, now + 0.95);
    gainNode.gain.linearRampToValueAtTime(0, now + 1.08);

    osc1.connect(gainNode);
    osc2.connect(gainNode);
    gainNode.connect(audioCtx.destination);
    osc1.start(now);
    osc2.start(now);
    osc1.stop(now + 1.12);
    osc2.stop(now + 1.12);
    activeOscillators = [osc1, osc2];
    activeGains = [gainNode];
  };

  const startVibration = () => {
    if (!navigator.vibrate) return;
    try {
      navigator.vibrate([700, 250, 700]);
      vibrationId = setInterval(() => {
        try { navigator.vibrate([700, 250, 700]); } catch (_) {
          // Vibration is optional and may be blocked by the browser.
        }
      }, 2500);
    } catch (_) {
      // Vibration is optional and may be blocked by the browser.
    }
  };

  const start = async () => {
    startVibration();
    try {
      audioCtx = getRingtoneAudioContext();
      if (!audioCtx) return;
      if (audioCtx.state !== 'running') {
        await audioCtx.resume();
      }
      if (audioCtx.state !== 'running') {
        console.warn('[Ringtone] Audio is blocked until the page receives a tap/click.');
        return;
      }
      ringBurst();
      intervalId = setInterval(ringBurst, 2200);
    } catch (e) {
      console.warn('[Ringtone] Web Audio not available:', e);
    }
  };

  const stop = () => {
    if (intervalId) clearInterval(intervalId);
    if (vibrationId) clearInterval(vibrationId);
    if (navigator.vibrate) {
      try { navigator.vibrate(0); } catch (_) {
        // Vibration may already be unavailable.
      }
    }
    stopTone();
    audioCtx = null;
    intervalId = null;
    vibrationId = null;
  };

  return { start, stop };
};

const IncomingCallBanner = () => {
  const at = useAutoTranslate();
  const { user, isAuthenticated, userPermissions } = useAuth();
  const canHandleCalls = canReceiveCalls({ ...user, permissions: userPermissions });
  const navigate = useNavigate();
  const [incomingCall, setIncomingCall] = useState(null);
  const [callState, setCallState] = useState('idle'); // idle, incoming, connected, ended
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [callerNumber, setCallerNumber] = useState('');
  const [callerCallSid, setCallerCallSid] = useState(null);
  const protectedContacts = hasProtectedContacts(user);
  const protectedContactsRef = useRef(protectedContacts);
  protectedContactsRef.current = protectedContacts;
  const receiverRef = useRef(null);
  const callDevice = useCallDevice(user.id);
  const [connection, setConnection] = useState({ state: 'connecting' });
  const [audioReady, setAudioReady] = useState(() => sharedRingtoneAudioCtx?.state === 'running');
  const [accepting, setAccepting] = useState(false);
  const [callError, setCallError] = useState('');
  const audioTestRef = useRef(null);
  const audioTestTimer = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const ringtoneRef = useRef(null);

  // Customer context state
  const [callerInfo, setCallerInfo] = useState(null);
  const [callerLoading, setCallerLoading] = useState(false);
  const [showContext, setShowContext] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteSending, setNoteSending] = useState(false);
  const [callNotes, setCallNotes] = useState([]); // Notes added during this call
  const [savedNotes, setSavedNotes] = useState([]); // Persisted notes from DB
  const [savedNotesLoading, setSavedNotesLoading] = useState(false);
  // Embed the caller's communication tunnel in the popup (CEO request):
  // whoever answers sees the full message history of exactly this caller.
  const [showCallerConv, setShowCallerConv] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState(''); // '', 'saving', 'saved'
  const autoSaveTimerRef = useRef(null);
  const lastAutoSavedRef = useRef(''); // track last autosaved text to avoid duplicate saves
  const autoSaveNoteIdRef = useRef(null); // ID of the autosaved note (for updating)

  // Mobile browsers block programmatic sound until the user taps/clicks the page once.
  useEffect(() => {
    const handleUnlockGesture = () => {
      if (sharedRingtoneAudioCtx?.state === 'running') return;
      unlockRingtoneAudio().then(setAudioReady);
    };

    const options = { passive: true };
    window.addEventListener('pointerdown', handleUnlockGesture, options);
    window.addEventListener('touchstart', handleUnlockGesture, options);
    window.addEventListener('click', handleUnlockGesture, options);
    window.addEventListener('keydown', handleUnlockGesture);

    return () => {
      window.removeEventListener('pointerdown', handleUnlockGesture, options);
      window.removeEventListener('touchstart', handleUnlockGesture, options);
      window.removeEventListener('click', handleUnlockGesture, options);
      window.removeEventListener('keydown', handleUnlockGesture);
    };
  }, []);

  // Start/stop ringtone based on call state
  useEffect(() => {
    if (callState === 'incoming') {
      ringtoneRef.current = createRingtone();
      ringtoneRef.current.start();
    } else {
      if (ringtoneRef.current) {
        ringtoneRef.current.stop();
        ringtoneRef.current = null;
      }
    }
    return () => {
      if (ringtoneRef.current) {
        ringtoneRef.current.stop();
        ringtoneRef.current = null;
      }
    };
  }, [callState]);

  // The same receiver is used by desktop and mobile. Resume/token events recover
  // registration after a sleeping tab; bounded retries expose an actionable status.
  useEffect(() => {
    if (!isAuthenticated || !user?.id || !canHandleCalls || !callDevice.preferences.enabled) {
      setConnection({ state: 'disabled' });
      return undefined;
    }
    const voiceIdentity = buildVoiceIdentity(user.id);
    const receiver = createVoiceReceiver({
      identity: voiceIdentity, userId: user.id,
      getToken: getGlobalVoiceToken, heartbeat: voiceAgentHeartbeat, leave: voiceAgentLeave,
      onStatus: setConnection,
      onIncoming: (call) => {
        setIncomingCall(call);
        setAccepting(false);
        setCallError('');
        const parentSid = call.customParameters?.get('ParentCallSid');
        const reference = call.customParameters?.get('VoiceOfferId') || parentSid || call.parameters?.CallSid;
        setCallerCallSid(parentSid || null);
        const display = parentSid ? 'Nummer verborgen' : (call.parameters?.From || 'Unknown');
        setCallerNumber(protectedContactsRef.current ? maskedPhone(display) : display);
        setCallState('incoming');
        setDuration(0);
        notifyCommunicationRefresh();
        // Permissions are requested by an explicit setup tap, never during a ring.
        showCallNotification(reference).catch(() => {});
        const closeNotice = () => closeCallNotifications(reference).catch(() => {});
        call.on('accept', () => {
          closeNotice();
          setAccepting(false);
          setCallState('connected');
          if (timerRef.current) clearInterval(timerRef.current);
          startTimeRef.current = Date.now();
          timerRef.current = setInterval(() => {
            setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
          }, 1000);
          notifyCommunicationRefresh();
        });
        ['cancel', 'reject'].forEach((event) => call.on(event, () => {
          closeNotice();
          setAccepting(false);
          setCallState('idle');
          setIncomingCall(null);
          notifyCommunicationRefresh();
        }));
        call.on('disconnect', () => {
          closeNotice();
          setAccepting(false);
          if (timerRef.current) clearInterval(timerRef.current);
          setCallState('ended');
          notifyCommunicationRefresh();
        });
        call.on('error', () => {
          closeNotice();
          setAccepting(false);
          if (timerRef.current) clearInterval(timerRef.current);
          setCallError('Gespräch konnte nicht verbunden werden. Mikrofon und Netzwerk bitte prüfen.');
          setCallState('ended');
          notifyCommunicationRefresh();
        });
      },
    });
    receiverRef.current = receiver;
    return () => {
      receiver.stop();
      receiverRef.current = null;
      if (timerRef.current) clearInterval(timerRef.current);
      closeCallNotifications().catch(() => {});
    };
  }, [isAuthenticated, user?.id, canHandleCalls, callDevice.preferences.enabled]);

  useEffect(() => {
    const inspectAudio = () => setAudioReady(sharedRingtoneAudioCtx?.state === 'running');
    document.addEventListener('visibilitychange', inspectAudio);
    window.addEventListener('voice-audio-state', inspectAudio);
    return () => {
      document.removeEventListener('visibilitychange', inspectAudio);
      window.removeEventListener('voice-audio-state', inspectAudio);
      clearTimeout(audioTestTimer.current);
      audioTestRef.current?.stop();
    };
  }, []);

  const handleAudioTest = async () => {
    setAudioReady(await unlockRingtoneAudio());
    audioTestRef.current?.stop();
    clearTimeout(audioTestTimer.current);
    audioTestRef.current = createRingtone();
    await audioTestRef.current.start();
    audioTestTimer.current = setTimeout(() => audioTestRef.current?.stop(), 1300);
  };

  const formatDuration = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleAccept = () => {
    if (incomingCall) {
      setAccepting(true);
      setCallError('');
      unlockRingtoneAudio().then(setAudioReady);
      try { incomingCall.accept(); }
      catch (_) {
        setAccepting(false);
        setCallError('Annahme fehlgeschlagen. Mikrofonfreigabe bitte prüfen.');
      }
    }
  };

  const handleReject = () => {
    if (incomingCall) {
      try { incomingCall.reject(); } catch (_) {
        // The remote caller may already have ended the call.
      }
    }
    cleanup();
  };

  const handleHangup = () => {
    if (incomingCall) {
      try { incomingCall.disconnect(); } catch (_) {
        // The call may already be disconnected.
      }
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setCallState('ended');
  };

  const handleMuteToggle = () => {
    if (incomingCall) {
      const newMuted = !isMuted;
      incomingCall.mute(newMuted);
      setIsMuted(newMuted);
    }
  };

  // Lookup caller info + saved notes when a call comes in
  useEffect(() => {
    if ((!callerCallSid && (!callerNumber || callerNumber === 'Unknown')) || callState === 'idle') {
      setCallerInfo(null);
      setSavedNotes([]);
      return;
    }
    let cancelled = false;
    const doLookup = async () => {
      setCallerLoading(true);
      setSavedNotesLoading(true);
      try {
        const [info, notesData] = await Promise.allSettled([
          callerLookup(callerNumber, callerCallSid),
          getContactNotes(callerNumber, callerCallSid),
        ]);
        if (!cancelled) {
          setCallerInfo(info.status === 'fulfilled' ? info.value : null);
          const number = info.status === 'fulfilled' && info.value?.customer?.phone;
          if (number) setCallerNumber(protectedContacts ? maskedPhone(number) : number);
          setSavedNotes(notesData.status === 'fulfilled' ? (notesData.value?.notes || []) : []);
        }
      } catch (err) {
        console.error('[IncomingCallBanner] Caller lookup failed:', err);
        if (!cancelled) { setCallerInfo(null); setSavedNotes([]); }
      } finally {
        if (!cancelled) { setCallerLoading(false); setSavedNotesLoading(false); }
      }
    };
    doLookup();
    return () => { cancelled = true; };
  }, [callerNumber, callerCallSid, callState, protectedContacts]);

  // ── Autosave: debounced 800ms, saves/updates note as user types ──
  const doAutoSave = useCallback(async (text) => {
    if (!text.trim() || (!callerCallSid && (!callerNumber || callerNumber === 'Unknown'))) return;
    if (text.trim() === lastAutoSavedRef.current) return; // no change
    try {
      setAutoSaveStatus('saving');
      const createdByName = user?.first_name
        ? `${user.first_name}${user.last_name ? ' ' + user.last_name : ''}`
        : 'Agent';
      const saved = await createContactNote({
        phone: callerCallSid ? undefined : callerNumber,
        call_sid: callerCallSid,
        content: `[Draft] ${text.trim()}`,
        customer_id: callerInfo?.customer?.customer_id || null,
        created_by_name: createdByName,
        replace_id: autoSaveNoteIdRef.current || undefined, // update existing draft
      });
      autoSaveNoteIdRef.current = saved?.id || autoSaveNoteIdRef.current;
      lastAutoSavedRef.current = text.trim();
      setAutoSaveStatus('saved');
    } catch (err) {
      console.error('[IncomingCallBanner] Autosave failed:', err);
      setAutoSaveStatus('');
    }
  }, [callerNumber, callerCallSid, callerInfo, user]);

  useEffect(() => {
    if (!noteText.trim() || callState === 'idle') return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setAutoSaveStatus('');
    autoSaveTimerRef.current = setTimeout(() => { doAutoSave(noteText); }, 800);
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
  }, [noteText, callState, doAutoSave]);

  // Save a note to the contact-notes API (persisted per phone number)
  const handleSendNote = async () => {
    if (!noteText.trim() || noteSending) return;
    setNoteSending(true);
    try {
      const createdByName = user?.first_name
        ? `${user.first_name}${user.last_name ? ' ' + user.last_name : ''}`
        : 'Agent';
      const saved = await createContactNote({
        phone: callerCallSid ? undefined : callerNumber,
        call_sid: callerCallSid,
        content: noteText.trim(),
        customer_id: callerInfo?.customer?.customer_id || null,
        created_by_name: createdByName,
        replace_id: autoSaveNoteIdRef.current || undefined, // replace draft with final
      });
      // Add to local state immediately
      setCallNotes(prev => [...prev, { text: noteText.trim(), time: new Date().toLocaleTimeString() }]);
      setSavedNotes(prev => [saved, ...prev.filter(n => n.id !== autoSaveNoteIdRef.current)]);
      setNoteText('');
      autoSaveNoteIdRef.current = null;
      lastAutoSavedRef.current = '';
      setAutoSaveStatus('');
    } catch (err) {
      console.error('[IncomingCallBanner] Failed to save note:', err);
    } finally {
      setNoteSending(false);
    }
  };

  const cleanup = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setCallState('idle');
    setIncomingCall(null);
    setDuration(0);
    setIsMuted(false);
    setCallerNumber('');
    setCallerCallSid(null);
    setCallerInfo(null);
    setCallNotes([]);
    setSavedNotes([]);
    setNoteText('');
    setShowContext(true);
    setIsMinimized(false);
    setAutoSaveStatus('');
    autoSaveNoteIdRef.current = null;
    lastAutoSavedRef.current = '';
  };

  const handleClose = () => {
    const phone = callerNumber;
    const conversationId = callerInfo?.conversations?.[0]?.id;
    const reference = callerCallSid;
    cleanup();
    if (reference && conversationId) {
      navigate(`/communication?conversation=${encodeURIComponent(conversationId)}&channel=WHATSAPP`);
      return;
    }
    if (reference || phone.includes('*') || phone === 'Nummer verborgen') return;
    if (phone && phone !== 'Unknown' && phone.trim() !== '') {
      const cleaned = phone.replace(/[^\d+]/g, '');
      if (cleaned) {
        navigate(`/communication?phone=${encodeURIComponent(cleaned)}&channel=WHATSAPP`);
      }
    }
  };

  // Determine whether a repair is still active (should appear during a call).
  // Hides finished/cancelled repairs and stale references to deleted processes.
  const isActiveRepair = (c) => {
    if (c.process_exists === false) return false;          // process was deleted
    if (c.cancelled === true) return false;                // cancelled repair
    const st = (c.process_status || c.status || '').toString().toLowerCase();
    const terminal = ['finished', 'completed', 'closed', 'archived', 'cancelled', 'canceled'];
    return !terminal.includes(st);
  };

  // Gather ALL unique ACTIVE repairs linked to this caller (deduped by repair_id).
  const repairIds = (callerInfo?.conversations || [])
    .filter(c => c.repair_id && isActiveRepair(c))
    .reduce((acc, c) => {
      if (!acc.find(x => x.repair_id === c.repair_id)) {
        acc.push({ repair_id: c.repair_id, process_id: c.process_id, status: c.process_status || c.status });
      }
      return acc;
    }, []);

  const customerName = callerInfo?.customer?.real_name || callerInfo?.customer?.display_name || null;
  const orderInvoices = callerInfo?.orders || [];

  // Determine whether caller is an Existing (known/returning) customer vs a New customer
  const isExistingCustomer = useMemo(() => {
    if (!callerInfo) return false;
    // 1. Linked to a registered CRM Actor / Customer ID
    if (callerInfo.customer?.customer_id) return true;
    // 2. Has known real customer name (not just generated "Client CLT-xxxx" alias)
    if (callerInfo.customer?.real_name) return true;
    // 3. Has existing orders, repairs, or saved notes
    if ((callerInfo.orders || []).length > 0) return true;
    if (repairIds.length > 0) return true;
    if (savedNotes.length > 0) return true;
    // 4. Has prior conversation history with actual messages (not just the current incoming call)
    const allConvs = callerInfo.conversations || [];
    if (allConvs.length > 1) return true;
    if (allConvs.length === 1) {
      const msgs = allConvs[0].recent_messages || [];
      const nonCallMsgs = msgs.filter(m => m.content !== 'Incoming call' && m.content !== 'Missed call');
      if (nonCallMsgs.length > 0 || msgs.length > 1) return true;
    }
    return false;
  }, [callerInfo, repairIds, savedNotes]);

  const hasMessageHistory = (conversation) =>
    (conversation?.recent_messages || []).some((message) => {
      const content = (message?.content || '').trim().toLowerCase();
      return content && content !== 'missed call' && content !== 'incoming call';
    });
  const activeConv = callerInfo?.conversations?.find(c => c.status === 'ACTIVE');
  const activeConvWithHistory = callerInfo?.conversations?.find(
    c => c.status === 'ACTIVE' && hasMessageHistory(c)
  );
  const historyConv = callerInfo?.conversations?.find(hasMessageHistory);
  // A new empty call thread should not hide the caller's older message history.
  const embeddedConvId =
    activeConvWithHistory?.conversation_id ||
    historyConv?.conversation_id ||
    activeConv?.conversation_id ||
    callerInfo?.conversations?.[0]?.conversation_id ||
    null;

  const deviceSettings = <CallDeviceSettings {...callDevice}
    audioReady={audioReady} connection={connection}
    inCall={callState === 'connected' || accepting}
    onAudioTest={handleAudioTest} onReconnect={() => receiverRef.current?.reconnect()} />;

  if (callState === 'idle') return deviceSettings;

  const accentColor = callState === 'connected' ? '#2563eb' : callState === 'ended' ? '#64748b' : '#16a34a';
  const accentLight = callState === 'connected' ? '#eff6ff' : callState === 'ended' ? '#f8fafc' : '#f0fdf4';
  const accentText = callState === 'connected' ? '#1d4ed8' : callState === 'ended' ? '#475569' : '#15803d';
  // The full conversation + reply tools only make sense once the call is answered.
  // While ringing we keep the popup compact so Accept/Reject stay front and centre.
  const showEmbeddedConv = !!embeddedConvId && showCallerConv && callState === 'connected';

  if (isMinimized) {
    return (
      <button
        type="button"
        onClick={() => setIsMinimized(false)}
        aria-label="Anruf-Popup wieder öffnen"
        title="Anruf-Popup wieder öffnen"
        style={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          minHeight: 42,
          padding: '8px 12px',
          border: `1px solid ${accentColor}40`,
          borderRadius: 12,
          background: '#fff',
          color: accentText,
          boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 800,
        }}
      >
        <TelephoneFill size={14} />
        {callState === 'incoming' ? 'Eingehender Anruf' : callState === 'connected' ? `Anruf ${formatDuration(duration)}` : 'Anruf beendet'}
      </button>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 20,
      right: 12,
      zIndex: 9999,
      width: showEmbeddedConv ? 'min(620px, calc(100vw - 24px))' : 'min(368px, calc(100vw - 24px))',
      maxHeight: 'calc(100vh - 40px)',
      overflowY: 'auto',
      borderRadius: 20,
      background: '#ffffff',
      boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 4px 16px rgba(0,0,0,0.08)',
      border: '1px solid rgba(0,0,0,0.06)',
      animation: callState === 'incoming' ? 'call-slide-in 0.4s cubic-bezier(0.34,1.56,0.64,1)' : 'none',
      transition: 'width 0.2s ease',
    }}>

      {callError && <div role="alert" style={{ padding: 12, color: '#9a3412', fontSize: 12 }}><T>{callError}</T></div>}

      {/* Top accent bar */}
      <div style={{
        height: 4,
        background: `linear-gradient(90deg, ${accentColor}, ${accentColor}cc)`,
      }} />

      <div style={{ padding: '18px 20px 16px' }}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>

          {/* Avatar */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            {callState === 'incoming' && (
              <>
                <div style={{ position: 'absolute', inset: -7, borderRadius: '50%', border: `2px solid ${accentColor}40`, animation: 'call-ring-1 2s infinite' }} />
                <div style={{ position: 'absolute', inset: -13, borderRadius: '50%', border: `2px solid ${accentColor}20`, animation: 'call-ring-2 2s infinite 0.4s' }} />
              </>
            )}
            <div style={{
              width: 54,
              height: 54,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 14px ${accentColor}40`,
            }}>
              {customerName
                ? <PersonFill size={26} color="#fff" />
                : <TelephoneInboundFill size={24} color="#fff" />
              }
            </div>
          </div>

          {/* Name + number + status + New/Existing badge */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <div style={{
                fontWeight: 800,
                fontSize: 16,
                color: '#0f172a',
                lineHeight: 1.25,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {customerName || 'Unknown Caller'}
              </div>
              {!callerLoading && callerNumber && callerNumber !== 'Unknown' && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3,
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '2px 7px',
                  borderRadius: 20,
                  background: isExistingCustomer ? '#eff6ff' : '#fef3c7',
                  color: isExistingCustomer ? '#1d4ed8' : '#b45309',
                  border: `1px solid ${isExistingCustomer ? '#bfdbfe' : '#fde68a'}`,
                  flexShrink: 0,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}>
                  {isExistingCustomer ? '● Existing' : '✨ New'}
                </span>
              )}
            </div>
            <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500, marginTop: 1 }}>
              {protectedContacts ? maskedPhone(callerNumber) : callerNumber}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 4 }}>
              <div style={{
                width: 7, height: 7, borderRadius: '50%',
                background: accentColor,
                animation: callState === 'incoming' ? 'call-blink 1.2s infinite' : 'none',
              }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: accentText, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {callState === 'incoming' ? 'Incoming Call' : callState === 'connected' ? 'On Call' : 'Call Ended'}
              </span>
              {callerLoading && <span style={{ fontSize: 10, color: '#94a3b8' }}><T>· Looking up…</T></span>}
            </div>

          </div>

          <button
            type="button"
            onClick={() => setIsMinimized(true)}
            aria-label="Anruf-Popup minimieren"
            title="Anruf-Popup minimieren"
            style={{
              width: 28,
              height: 28,
              flexShrink: 0,
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              background: '#f8fafc',
              color: '#64748b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: 1,
              fontWeight: 700,
            }}
          >
            −
          </button>

          {/* Timer (connected) */}
          {callState === 'connected' && (
            <div style={{
              background: accentLight,
              color: accentText,
              borderRadius: 10,
              padding: '4px 10px',
              fontSize: 15,
              fontWeight: 800,
              fontVariantNumeric: 'tabular-nums',
              flexShrink: 0,
              border: `1px solid ${accentColor}30`,
            }}>
              {formatDuration(duration)}
            </div>
          )}
        </div>

        {/* ── Repair badges ── */}
        {repairIds.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
            {repairIds.map(item => (
              <div
                key={item.repair_id}
                onClick={() => item.process_id && window.open(`/repair-processes/${item.process_id}`, '_blank')}
                style={{
                  background: '#fff7ed',
                  color: '#c2410c',
                  border: '1px solid #fed7aa',
                  borderRadius: 8,
                  padding: '3px 10px',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: item.process_id ? 'pointer' : 'default',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => { if (item.process_id) e.currentTarget.style.background = '#ffedd5'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff7ed'; }}
              >
                🔧 {item.repair_id}
                {item.status && !['ONGOING', 'ongoing'].includes(item.status) && (
                  <span style={{ fontSize: 9, opacity: 0.65 }}>({item.status})</span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── Order badges ── */}
        {orderInvoices.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
            {orderInvoices.map(order => (
              <div
                key={order.invoice_no}
                onClick={() => order.order_id ? window.open(`/orders/${order.order_id}`, '_blank') : window.open('/orders', '_blank')}
                style={{
                  background: '#eff6ff',
                  color: '#1d4ed8',
                  border: '1px solid #bfdbfe',
                  borderRadius: 8,
                  padding: '3px 10px',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#dbeafe'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#eff6ff'; }}
              >
                📄 #{order.invoice_no}
              </div>
            ))}
          </div>
        )}

        {/* ── Notes panel ── */}
        {callState !== 'idle' && (
          <div style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 14,
            padding: '10px 12px',
            marginBottom: 14,
          }}>
            {/* Notes header */}
            <div
              onClick={() => setShowContext(!showContext)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: 8 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#374151' }}>
                <JournalText size={13} color="#6366f1" /><T>
                Notes
                </T>{(savedNotes.length + callNotes.length) > 0 && (
                  <span style={{
                    background: '#6366f1',
                    color: '#fff',
                    borderRadius: 20,
                    padding: '0 6px',
                    fontSize: 10,
                    fontWeight: 800,
                  }}>{savedNotes.length + callNotes.length}</span>
                )}
              </div>
              <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{showContext ? 'Hide' : 'Show'}</span>
            </div>

            {/* Note input */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder={at("Add a note about this call…")}
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendNote(); } }}
                disabled={noteSending}
                className="call-note-input"
                style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  color: '#0f172a',
                  borderRadius: 10,
                  fontSize: 13,
                  resize: 'none',
                  flex: 1,
                  lineHeight: 1.5,
                }}
              />
              <button
                onClick={handleSendNote}
                disabled={!noteText.trim() || noteSending}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  border: 'none',
                  background: noteText.trim() ? '#6366f1' : '#e2e8f0',
                  color: noteText.trim() ? '#fff' : '#94a3b8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: noteText.trim() ? 'pointer' : 'default',
                  flexShrink: 0,
                  transition: 'background 0.15s',
                }}
              >
                <SendFill size={14} />
              </button>
            </div>

            {autoSaveStatus && (
              <div style={{ fontSize: 10, marginTop: 5, color: autoSaveStatus === 'saved' ? '#16a34a' : '#d97706', fontWeight: 600 }}>
                {autoSaveStatus === 'saving' ? '⏳ Autosaving…' : '✓ Draft saved'}
              </div>
            )}

            {/* Notes list */}
            {showContext && (
              <div style={{ maxHeight: 220, overflowY: 'auto', marginTop: 8 }}>
                {callNotes.length > 0 && (
                  <>
                    <div style={{ fontSize: 10, color: '#16a34a', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}><T>This call</T></div>
                    {callNotes.map((n, i) => (
                      <div key={`new-${i}`} style={{
                        padding: '7px 10px',
                        marginBottom: 4,
                        background: '#f0fdf4',
                        borderRadius: 8,
                        fontSize: 13,
                        lineHeight: 1.4,
                        borderLeft: '3px solid #22c55e',
                      }}>
                        <div style={{ fontSize: 10, color: '#86efac', marginBottom: 2 }}>{n.time}</div>
                        <div style={{ whiteSpace: 'pre-wrap', color: '#0f172a' }}>{n.text}</div>
                      </div>
                    ))}
                  </>
                )}
                {savedNotesLoading ? (
                  <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', padding: '8px 0' }}><T>Loading notes…</T></div>
                ) : savedNotes.length > 0 ? (
                  <>
                    {callNotes.length > 0 && (
                      <div style={{ fontSize: 10, color: '#6366f1', fontWeight: 700, marginTop: 6, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}><T>Previous</T></div>
                    )}
                    {savedNotes.filter(n => !callNotes.some(cn => cn.text === n.content)).map(n => (
                      <div key={n.id} style={{
                        padding: '7px 10px',
                        marginBottom: 4,
                        background: '#fff',
                        borderRadius: 8,
                        fontSize: 13,
                        lineHeight: 1.4,
                        border: '1px solid #e2e8f0',
                        borderLeft: '3px solid #6366f1',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#94a3b8', marginBottom: 3 }}>
                          <span style={{ fontWeight: 600, color: '#6366f1' }}>{n.created_by_name || 'Agent'}</span>
                          <span>{n.created_at ? new Date(n.created_at).toLocaleString('de-CH', { timeZone: 'Europe/Zurich' }) : ''}</span>
                        </div>
                        <div style={{ whiteSpace: 'pre-wrap', color: '#374151' }}>{n.content}</div>
                      </div>
                    ))}
                  </>
                ) : (
                  callNotes.length === 0 && (
                    <div style={{ fontSize: 12, color: '#cbd5e1', textAlign: 'center', padding: '10px 0' }}><T>No notes yet for this contact</T></div>
                  )
                )}
              </div>
            )}
          </div>
        )}

        {/* ── While ringing: compact hint that context opens on Accept ── */}
        {callState === 'incoming' && embeddedConvId && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 12,
            padding: '9px 12px', marginBottom: 14, fontSize: 12, color: '#64748b', fontWeight: 600,
          }}><T>
            💬 Conversation history & reply tools open when you accept.
          </T></div>
        )}

        {/* ── Caller's communication tunnel (embedded) — only after answering ── */}
        {embeddedConvId && callState === 'connected' && <LiveCallCoach conversationId={embeddedConvId} callSid={incomingCall?.parameters?.CallSid} />}
        {embeddedConvId && callState === 'connected' && (
          <div style={{ marginBottom: 14 }}>
            <div
              onClick={() => setShowCallerConv(v => !v)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                cursor: 'pointer', marginBottom: 6, padding: '0 2px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#374151' }}><T>
                💬 Conversation
              </T></div>
              <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{showCallerConv ? 'Hide' : 'Show'}</span>
            </div>
            {showCallerConv && (
              <div style={{ height: 'clamp(380px, 54vh, 580px)', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                <ConversationView
                  conversationId={embeddedConvId}
                  embedded
                  onBack={() => setShowCallerConv(false)}
                />
              </div>
            )}
          </div>
        )}

        {/* Unknown caller note */}
        {!callerLoading && callerInfo && !callerInfo.found && callState !== 'idle' && (
          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 5 }}>
            <span>⚠</span><T> Unknown caller — no matching customer found
          </T></div>
        )}

        {/* ── Action buttons ── */}
        {callState === 'incoming' && (
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={handleReject}
              style={{
                flex: 1,
                height: 48,
                borderRadius: 14,
                border: 'none',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                color: '#fff',
                fontSize: 14,
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 7,
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(239,68,68,0.35)',
                transition: 'transform 0.1s, box-shadow 0.1s',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.02)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <TelephoneXFill size={16} /><T> Ablehnen
            </T></button>
            <button
              onClick={handleAccept}
              disabled={accepting}
              style={{
                flex: 1,
                height: 48,
                borderRadius: 14,
                border: 'none',
                background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                color: '#fff',
                fontSize: 14,
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 7,
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(34,197,94,0.35)',
                transition: 'transform 0.1s, box-shadow 0.1s',
                animation: 'accept-pulse 2s infinite',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.02)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <TelephoneFill size={16} /> {accepting ? 'Verbinden…' : 'Annehmen'}
            </button>
          </div>
        )}

        {callState === 'connected' && <CallAudioOutput audio={receiverRef.current?.getAudio?.()} />}

        {callState === 'connected' && (
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={handleMuteToggle}
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                border: `2px solid ${isMuted ? '#f59e0b' : '#e2e8f0'}`,
                background: isMuted ? '#fef3c7' : '#f8fafc',
                color: isMuted ? '#d97706' : '#64748b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                flexShrink: 0,
                transition: 'all 0.15s',
              }}
            >
              {isMuted ? <MicMuteFill size={18} /> : <MicFill size={18} />}
            </button>
            <button
              onClick={handleHangup}
              style={{
                flex: 1,
                height: 48,
                borderRadius: 14,
                border: 'none',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                color: '#fff',
                fontSize: 14,
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 7,
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(239,68,68,0.3)',
              }}
            >
              <TelephoneXFill size={16} /><T> Auflegen
            </T></button>
          </div>
        )}

        {callState === 'ended' && (
          <button
            onClick={handleClose}
            style={{
              width: '100%',
              height: 44,
              borderRadius: 14,
              background: callerNumber && callerNumber !== 'Unknown' && callerNumber.trim() !== '' ? 'linear-gradient(135deg, #128c7e 0%, #25d366 100%)' : '#f8fafc',
              color: callerNumber && callerNumber !== 'Unknown' && callerNumber.trim() !== '' ? '#fff' : '#475569',
              border: callerNumber && callerNumber !== 'Unknown' && callerNumber.trim() !== '' ? 'none' : '1.5px solid #e2e8f0',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'all 0.15s',
              boxShadow: callerNumber && callerNumber !== 'Unknown' && callerNumber.trim() !== '' ? '0 4px 12px rgba(37,211,102,0.2)' : 'none',
            }}
            onMouseEnter={e => {
              if (callerNumber && callerNumber !== 'Unknown' && callerNumber.trim() !== '') {
                e.currentTarget.style.transform = 'scale(1.02)';
              } else {
                e.currentTarget.style.background = '#f1f5f9';
              }
            }}
            onMouseLeave={e => {
              if (callerNumber && callerNumber !== 'Unknown' && callerNumber.trim() !== '') {
                e.currentTarget.style.transform = 'scale(1)';
              } else {
                e.currentTarget.style.background = '#f8fafc';
              }
            }}
          >
            {callerNumber && callerNumber !== 'Unknown' && callerNumber.trim() !== '' ? 'Dismiss & Open WhatsApp' : 'Dismiss'}
          </button>
        )}
      </div>

      <style>{`
        @keyframes call-slide-in {
          from { opacity: 0; transform: translateX(40px) scale(0.95); }
          to   { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes call-ring-1 {
          0%   { transform: scale(1);   opacity: 0.6; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        @keyframes call-ring-2 {
          0%   { transform: scale(1);   opacity: 0.4; }
          100% { transform: scale(1.9); opacity: 0; }
        }
        @keyframes call-blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.3; }
        }
        @keyframes accept-pulse {
          0%,100% { box-shadow: 0 4px 14px rgba(34,197,94,0.35); }
          50%     { box-shadow: 0 4px 22px rgba(34,197,94,0.6); }
        }
        .call-note-input,
        .call-note-input:focus {
          background: #fff !important;
          color: #0f172a !important;
          border-color: #e2e8f0 !important;
          box-shadow: none !important;
          outline: none !important;
        }
        .call-note-input:focus {
          border-color: #6366f1 !important;
        }
        .call-note-input::placeholder {
          color: #cbd5e1 !important;
        }
      `}</style>
    </div>
  );
};

export default IncomingCallBanner;
